import './App.css';
import { useState, useEffect } from 'react';
import Login from './Login';
import Signup from './Signup';
import TransfertList from './TransfertList';
import TransfertForm from './TransfertForm';
import GuidePage from './GuidePage';
import AgreementPage from './AgreementPage';
import MaintenancePage from './MaintenancePage';
import ChatPage from './ChatPage';
import { normalizeAuthToken, extractOwnerId } from './utils/auth';
import { fetchWithAuthFallback } from './utils/api';
import { normalizeTransferDossiers } from './utils/transferts';

// Passer à true pour activer le mode maintenance
const MAINTENANCE_MODE = false;
const API_BASE_URL = 'https://admin.cgiibnn-esursi.cd/api/candidat';

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [currentView, setCurrentView] = useState('list'); // 'list', 'form', 'guide', 'agreement', 'chat'
  const [authView, setAuthView] = useState('login'); // 'login' ou 'signup'
  const [editingTransfert, setEditingTransfert] = useState(null);
  const [chatUnreadCount, setChatUnreadCount] = useState(0);
  const [hasTransferDossiers, setHasTransferDossiers] = useState(false);

  const canAccessChat = user?.est_admin_transfert === true || hasTransferDossiers;

  // Vérifier si l'utilisateur est déjà connecté au chargement
  useEffect(() => {
    const storedToken = sessionStorage.getItem('token') || localStorage.getItem('token');
    const storedUserString = sessionStorage.getItem('user') || localStorage.getItem('user');

    if (!storedUserString) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      sessionStorage.removeItem('token');
      sessionStorage.removeItem('user');
      return;
    }

    let parsedUser = null;
    try {
      parsedUser = JSON.parse(storedUserString);
    } catch (error) {
      parsedUser = null;
    }

    if (!parsedUser) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      sessionStorage.removeItem('token');
      sessionStorage.removeItem('user');
      return;
    }

    const ownerId = extractOwnerId(parsedUser);
    if (ownerId !== null && ownerId !== undefined) {
      if (parsedUser.id === undefined) {
        parsedUser.id = ownerId;
      }
      if (parsedUser.candidat_id === undefined) {
        parsedUser.candidat_id = ownerId;
      }
    }

    const normalizedToken = normalizeAuthToken(storedToken);
    const sessionOwnerId = extractOwnerId(parsedUser);
    const hasOwnerId = sessionOwnerId !== null && sessionOwnerId !== undefined;

    if (hasOwnerId) {
      if (normalizedToken && normalizedToken !== storedToken) {
        sessionStorage.setItem('token', normalizedToken);
      } else if (normalizedToken && !sessionStorage.getItem('token')) {
        sessionStorage.setItem('token', normalizedToken);
      } else if (!normalizedToken) {
        sessionStorage.removeItem('token');
      }
      sessionStorage.setItem('user', JSON.stringify(parsedUser));
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      setToken(normalizedToken);
      setUser(parsedUser);
      setIsAuthenticated(true);
    } else {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      sessionStorage.removeItem('token');
      sessionStorage.removeItem('user');
    }
  }, []);

  useEffect(() => {
    if (!isAuthenticated || !user) {
      setHasTransferDossiers(false);
      return;
    }

    if (user.est_admin_transfert === true) {
      setHasTransferDossiers(true);
      return;
    }

    const currentCandidatId = extractOwnerId(user);
    if (!currentCandidatId) {
      setHasTransferDossiers(false);
      return;
    }

    const fetchTransferDossiers = async () => {
      try {
        const response = await fetchWithAuthFallback(`${API_BASE_URL}/transfert/demandes/?current_candidat_id=${currentCandidatId}`, {
          method: 'GET',
          headers: {
            Accept: 'application/json',
          },
        }, token);

        if (!response.ok) {
          setHasTransferDossiers(false);
          return;
        }

        const result = await response.json();
        const transferts = normalizeTransferDossiers(result);
        setHasTransferDossiers(Array.isArray(transferts) && transferts.length > 0);
      } catch (error) {
        setHasTransferDossiers(false);
      }
    };

    fetchTransferDossiers();
  }, [isAuthenticated, token, user]);

  useEffect(() => {
    if (!isAuthenticated || !user) {
      setChatUnreadCount(0);
      return undefined;
    }

    const currentCandidatId = extractOwnerId(user);
    if (!currentCandidatId) {
      setChatUnreadCount(0);
      return undefined;
    }

    const fetchUnreadCount = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/chat/conversations/?current_candidat_id=${currentCandidatId}`, {
          method: 'GET',
          headers: {
            Accept: 'application/json',
            ...(token ? { Authorization: token } : {}),
          },
        });

        if (!response.ok) {
          return;
        }

        const payload = await response.json();
        const conversations = Array.isArray(payload?.results) ? payload.results : [];
        const totalUnread = conversations.reduce((total, conversation) => (
          total + Number(conversation.non_lus || 0)
        ), 0);
        setChatUnreadCount(totalUnread);
      } catch (error) {
        // Garder la dernière valeur connue en cas d'erreur réseau ponctuelle.
      }
    };

    fetchUnreadCount();
    const intervalId = window.setInterval(fetchUnreadCount, 30000);
    return () => window.clearInterval(intervalId);
  }, [isAuthenticated, token, user]);

  const handleLoginSuccess = (userData, token) => {
    const normalizedToken = normalizeAuthToken(token);
    setUser(userData);
    setToken(normalizedToken);
    setIsAuthenticated(true);
    setCurrentView('list');
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    sessionStorage.removeItem('token');
    sessionStorage.removeItem('user');
    setUser(null);
    setToken(null);
    setIsAuthenticated(false);
    setCurrentView('list');
    setEditingTransfert(null);
    setChatUnreadCount(0);
    setHasTransferDossiers(false);
  };

  const handleCreateNew = () => {
    setEditingTransfert(null);
    setCurrentView('agreement'); // Afficher d'abord la page d'accord
  };

  const handleEditTransfert = (transfertId, transfertData) => {
    setEditingTransfert({
      id: transfertId,
      data: transfertData,
    });
    setCurrentView('form'); // Aller directement au formulaire
  };

  const handleBackToList = () => {
    setCurrentView('list');
    setEditingTransfert(null);
  };

  const handleShowGuide = () => {
    setEditingTransfert(null);
    setCurrentView('guide');
  };

  const handleOpenChat = async () => {
    if (!canAccessChat) {
      return;
    }

    if (user?.est_admin_transfert !== true) {
      const currentCandidatId = extractOwnerId(user);

      if (!currentCandidatId) {
        window.alert('Accès refusé : identifiant candidat introuvable.');
        return;
      }

      try {
        const response = await fetchWithAuthFallback(`${API_BASE_URL}/transfert/demandes/?current_candidat_id=${currentCandidatId}`, {
          method: 'GET',
          headers: {
            Accept: 'application/json',
          },
        }, token);

        if (!response.ok) {
          window.alert('Impossible de vérifier vos demandes de transfert pour accéder à la messagerie.');
          return;
        }

        const result = await response.json();
        const transferts = normalizeTransferDossiers(result);

        if (!Array.isArray(transferts) || transferts.length === 0) {
          window.alert('Accès refusé : vous devez d’abord disposer d’un dossier de demande de transfert pour utiliser la messagerie.');
          return;
        }
      } catch (error) {
        window.alert(`Erreur réseau : ${error.message}`);
        return;
      }
    }

    setEditingTransfert(null);
    setCurrentView('chat');
  };

  const handleAgreementContinue = () => {
    setCurrentView('guide'); // Aller à la page guide après acceptation
  };

  const handleAgreementQuit = () => {
    setCurrentView('list'); // Retourner à la liste
    setEditingTransfert(null);
  };

  const handleGuideContinue = () => {
    setCurrentView('form'); // Aller au formulaire après avoir lu le guide
  };

  const handleSignupSuccess = (token, userData) => {
    const normalizedToken = normalizeAuthToken(token);
    setUser(userData);
    setToken(normalizedToken);
    setIsAuthenticated(true);
    setCurrentView('list');
    setAuthView('login');
  };

  if (MAINTENANCE_MODE) {
    return <MaintenancePage />;
  }

  return (
    <div className="App">
      {isAuthenticated ? (
        <>
          {currentView === 'list' && (
            <TransfertList
              user={user}
              token={token}
              onLogout={handleLogout}
              onCreateNew={handleCreateNew}
              onEditTransfert={handleEditTransfert}
              onShowGuide={handleShowGuide}
              onOpenChat={handleOpenChat}
              chatUnreadCount={chatUnreadCount}
              canAccessChat={canAccessChat}
              onTransferDossiersChange={setHasTransferDossiers}
            />
          )}

          {currentView === 'agreement' && (
            <AgreementPage
              user={user}
              onContinue={handleAgreementContinue}
              onQuit={handleAgreementQuit}
              onLogout={handleLogout}
            />
          )}

          {currentView === 'form' && (
            <TransfertForm
              user={user}
              token={token}
              onLogout={handleLogout}
              editingTransfert={editingTransfert}
              onBackToList={handleBackToList}
              onOpenChat={handleOpenChat}
              chatUnreadCount={chatUnreadCount}
              canAccessChat={canAccessChat}
            />
          )}

          {currentView === 'guide' && (
            <GuidePage
              user={user}
              onBack={handleBackToList}
              onContinue={handleGuideContinue}
              onLogout={handleLogout}
              fromAgreement={editingTransfert !== null || currentView === 'guide'}
            />
          )}

          {currentView === 'chat' && (
            <ChatPage
              user={user}
              onBack={handleBackToList}
              onLogout={handleLogout}
              onUnreadCountChange={setChatUnreadCount}
            />
          )}
        </>
      ) : authView === 'login' ? (
        <Login 
          onLoginSuccess={handleLoginSuccess}
          onShowSignup={() => setAuthView('signup')}
        />
      ) : (
        <Signup 
          onSignupSuccess={handleSignupSuccess}
          onBackToLogin={() => setAuthView('login')}
        />
      )}
    </div>
  );
}

export default App;
